import { useEffect, useState } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { roundClock } from './clock';

/** How often text readouts refresh while a round is live (~12 fps is plenty for digits). */
export const LIVE_MULTIPLIER_INTERVAL_MS = 80;

/**
 * The multiplier to show in text: the same prediction the canvas draws each
 * frame, refreshed at a UI-friendly rate while the round is live.
 *
 * The context's `currentMultiplier` only changes on the once-a-second server
 * correction, so reading it directly makes the number, live return and cash-out
 * button step once a second behind the skier. Outside a live round (and in the
 * local dev sandbox) the context value is authoritative and is returned as is:
 * crash points and settled values always come from the server.
 */
export function useLiveMultiplier(): number {
    const { currentMultiplier, gameInfo, devSimulation } = useSocket();
    const live = gameInfo?.phase === 'IN_PROGRESS' && !devSimulation?.active;
    // Only drives re-renders; the value itself is read fresh from the clock below,
    // so a new round never flashes the previous round's last reading.
    const [, setShownCents] = useState(0);

    useEffect(() => {
        if (!live) return;
        // Re-renders only when the displayed (two-decimal) value moves.
        const tick = () => setShownCents(Math.round(roundClock.multiplier() * 100));
        tick();
        const id = window.setInterval(tick, LIVE_MULTIPLIER_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [live]);

    if (!live || !roundClock.isRunning) return currentMultiplier;
    return Math.max(roundClock.multiplier(), currentMultiplier);
}
